import React, { useState, useRef } from "react";
import XLSX from "xlsx";
import LoadingSpinner from "../../loaders/loadingSpinner";
import { GeneralButton } from "../index";

/**
 * Allowed file types - aligned with backend RequestMiddleware
 */
const DEFAULT_ACCEPTED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint', // .ppt
  'application/pdf', // .pdf
  'image/png', // .png
  'image/jpeg', // .jpg, .jpeg
];

const ALLOWED_EXTENSIONS = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'pdf', 'png', 'jpg', 'jpeg'];

const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB per file
const DEFAULT_MAX_TOTAL_SIZE = 35 * 1024 * 1024; // 35MB total

function ExcelFileUploader({ 
  handleUploadItems, 
  label = null, 
  customButton = false,
  acceptedMimeTypes = DEFAULT_ACCEPTED_MIME_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
  onValidationError = null,
  showAllowedTypes = true,
}) {
  const [loader, setLoader] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const inputFile = useRef(null);

  const spreadSheetFileTypes = ["xls", "xlsx", "ods"]; // Files that can be parsed as Excel

  /**
   * Format bytes to human-readable string
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Get file extension
   */
  const getExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
  };

  /**
   * Validate a single file
   */
  const validateFile = (file) => {
    const errors = [];
    const extension = getExtension(file.name);

    // 1. Validate extension
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      errors.push(`File type ".${extension}" is not allowed`);
    }

    // 2. Validate MIME type (if available)
    if (file.type && !acceptedMimeTypes.includes(file.type)) {
      // Don't double-report if extension already caught it
      if (ALLOWED_EXTENSIONS.includes(extension)) {
        errors.push(`Invalid file type "${file.type}"`);
      }
    }

    // 3. Validate size
    if (file.size > maxFileSize) {
      errors.push(`File size (${formatFileSize(file.size)}) exceeds limit of ${formatFileSize(maxFileSize)}`);
    }

    return { valid: errors.length === 0, errors };
  };

  /**
   * Validate all files
   */
  const validateAllFiles = (files) => {
    const errors = [];
    const validFiles = [];
    let totalSize = 0;

    Array.from(files).forEach((file) => {
      const validation = validateFile(file);
      
      if (validation.valid) {
        validFiles.push(file);
        totalSize += file.size;
      } else {
        validation.errors.forEach(err => {
          errors.push(`"${file.name}": ${err}`);
        });
      }
    });

    // Check total size
    if (totalSize > maxTotalSize) {
      errors.push(`Total size (${formatFileSize(totalSize)}) exceeds limit of ${formatFileSize(maxTotalSize)}`);
    }

    return { isValid: errors.length === 0, errors, validFiles, totalSize };
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    
    if (!files || files.length === 0) return;

    // Clear previous errors
    setValidationErrors([]);

    // Validate files
    const validation = validateAllFiles(files);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      if (onValidationError) {
        onValidationError(validation.errors);
      }

      // If no valid files, stop here
      if (validation.validFiles.length === 0) {
        if (inputFile.current) inputFile.current.value = null;
        return;
      }
    }

    setLoader(true);

    // Build filesArray from valid files only
    const filesArray = validation.validFiles.map((file) => ({
      row: null,
      name: file.name,
      status: "queue",
      obj: file,
    }));

    // Find Excel file for parsing
    const excelFile = filesArray.find((file) =>
      spreadSheetFileTypes.includes(getExtension(file.name))
    );

    if (excelFile) {
      const reader = new FileReader();
      const rABS = !!reader.readAsBinaryString;

      reader.onload = (e) => {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, {
          type: rABS ? "binary" : "array",
          bookVBA: true,
        });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        handleUploadItems(
          data.filter(row => typeof Object.values(row)[0] === 'string' ? !Object.values(row)[0].includes('*** Remove') : true),
          filesArray
        );
        setLoader(false);
      };

      if (rABS) {
        reader.readAsBinaryString(excelFile.obj);
      } else {
        reader.readAsArrayBuffer(excelFile.obj);
      }
    } else {
      handleUploadItems([], filesArray);
      setLoader(false);
    }

    if (inputFile.current) inputFile.current.value = null;
  };

  const handleOnDrag = (e) => {
    e.preventDefault();
  };

  const handleOnDrop = (e) => {
    handleFileChange({ target: { files: e.dataTransfer.files } });
    e.preventDefault();
  };

  /**
   * Render validation errors
   */
  const renderValidationErrors = () => {
    if (validationErrors.length === 0) return null;

    return (
      <div
        style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '4px',
          padding: '10px 12px',
          marginBottom: '10px',
          color: '#dc2626',
          fontSize: '14px',
        }}
      >
        <strong>⚠️ Please fix the following errors:</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          {validationErrors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  };

  /**
   * Render allowed types hint
   */
  const renderAllowedTypesHint = () => {
    if (!showAllowedTypes) return null;

    return (
      <div
        style={{
          fontSize: '12px',
          color: '#6b7280',
          marginTop: '8px',
          textAlign: 'center',
        }}
      >
        Allowed: .{ALLOWED_EXTENSIONS.join(', .')}
      </div>
    );
  };

  return customButton === false ? (
    <>
      {renderValidationErrors()}
      
      <div 
        className="col-lg-12 b-dashed-2 bg-light rounded-lg py-5" 
        onDrop={handleOnDrop} 
        onDragOver={handleOnDrag}
      >
        <div style={{ position: "relative", top: "50%", left: "50%", zIndex: "100" }}>
          {loader === true && <LoadingSpinner />}
        </div>
        <div className="d-block text-center">
          <label htmlFor="file" className="btn btn-secondary bulk_upload_btn">
            <i className="fa fa-folder"></i>&nbsp;
            {label != null ? label : "Browse File"}
          </label>
        </div>
      </div>

      {renderAllowedTypesHint()}

      <input 
        ref={inputFile} 
        type="file" 
        className="form-control" 
        id="file" 
        onChange={handleFileChange} 
        style={{ visibility: "hidden" }} 
        multiple 
      />
    </>
  ) : (
    <>
      {renderValidationErrors()}
      
      <div style={{ position: "relative", top: "50%", left: "50%", zIndex: "100" }}>
        {loader === true && <LoadingSpinner />}
      </div>
      <GeneralButton 
        onClick={(e) => inputFile.current.click()} 
        className="btn btn-secondary btn-sm bulk_upload_btn" 
        faIcon="fa fa-upload" 
        name={label ? label : "Bulk Upload"}
      />
      
      {renderAllowedTypesHint()}
      
      <input 
        ref={inputFile} 
        type="file" 
        className="form-control" 
        id="file" 
        onChange={handleFileChange} 
        style={{ visibility: "hidden" }} 
        multiple 
      />
    </>
  );
}

export default ExcelFileUploader;